import React, { useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, XIcon, DownloadIcon } from './icons/InterfaceIcons';
import { parseTextWithImages } from './parseTextWithImages';

const QuestionResultCard: React.FC<{ question: any, userAnswer: any, questionIndex: number }> = ({ question, userAnswer, questionIndex }) => {
    // Handle different data structures
    const options = question.Options || question.options || [
        question.option1, question.option2, question.option3, question.option4
    ].filter(Boolean);
    
    let correctAnswer = question.Answer || question.answer || question.correct;
    if (correctAnswer && correctAnswer.startsWith('option')) {
        const optionNum = parseInt(correctAnswer.replace('option', '')) - 1;
        correctAnswer = options[optionNum];
    }
    
    const isCorrect = userAnswer === correctAnswer;
    const isSkipped = userAnswer === undefined || userAnswer === null;
    const [showExplanation, setShowExplanation] = useState(false);
    
    const getResultIcon = () => {
        if (isSkipped) return <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">?</div>;
        if (isCorrect) return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
        return <XIcon className="w-6 h-6 text-red-500" />;
    };
    
    const getResultColor = () => {
        if (isSkipped) return 'border-gray-300 bg-gray-50';
        if (isCorrect) return 'border-green-300 bg-green-50';
        return 'border-red-300 bg-red-50';
    };
    
    return (
        <div style={{ border: '2px solid', borderRadius: '12px', padding: '24px', backgroundColor: '#ffffff', borderColor: isSkipped ? '#d1d5db' : isCorrect ? '#10b981' : '#ef4444' }}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                    {getResultIcon()}
                    <div>
                        <h3 style={{ fontWeight: '600', fontSize: '18px', color: '#1f2937' }}>Question {questionIndex + 1}</h3>
                        {question.Subject && (
                            <span className="text-xs px-2 py-1 bg-brand-primary/10 text-brand-primary rounded-full">
                                {question.Subject}
                            </span>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <div className={`text-sm font-semibold ${
                        isCorrect ? 'text-green-600' : isSkipped ? 'text-gray-600' : 'text-red-600'
                    }`}>
                        {isCorrect ? '+4 marks' : isSkipped ? '0 marks' : '-1 mark'}
                    </div>
                </div>
            </div>
            
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ color: '#1f2937', marginBottom: '16px' }}>{parseTextWithImages(question.Text || question.text || question.Question || question.question)}</div>
            </div>
            
            <div className="space-y-2 mb-4">
                {options.map((option, index) => {
                    const isCorrectAnswer = option === correctAnswer;
                    const isUserChoice = option === userAnswer;
                    let optionStyle = 'border-brand-border bg-white';
                    let iconStyle = 'border-gray-300';

                    if (isCorrectAnswer) {
                        optionStyle = 'border-green-500 bg-green-100 text-green-800';
                        iconStyle = 'border-green-500 bg-green-500 text-white';
                    } else if (isUserChoice && !isCorrectAnswer) {
                        optionStyle = 'border-red-500 bg-red-100 text-red-800';
                        iconStyle = 'border-red-500 bg-red-500 text-white';
                    }

                    return (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '12px',
                            border: '2px solid',
                            borderRadius: '8px',
                            borderColor: isCorrectAnswer ? '#10b981' : isUserChoice && !isCorrectAnswer ? '#ef4444' : '#e5e7eb',
                            backgroundColor: isCorrectAnswer ? '#ecfdf5' : isUserChoice && !isCorrectAnswer ? '#fef2f2' : '#ffffff'
                        }}>
                            <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                border: '2px solid',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '12px',
                                flexShrink: 0,
                                borderColor: isCorrectAnswer ? '#10b981' : isUserChoice && !isCorrectAnswer ? '#ef4444' : '#9ca3af',
                                backgroundColor: isCorrectAnswer ? '#10b981' : isUserChoice && !isCorrectAnswer ? '#ef4444' : 'transparent',
                                color: isCorrectAnswer || (isUserChoice && !isCorrectAnswer) ? '#ffffff' : '#1f2937'
                            }}>
                                <span style={{ fontSize: '12px', fontWeight: '600' }}>{String.fromCharCode(65 + index)}</span>
                            </div>
                            <div className="flex-1">{parseTextWithImages(option)}</div>
                            {isCorrectAnswer && <CheckCircleIcon className="w-5 h-5 ml-2" style={{ color: '#10b981' }} />}
                            {isUserChoice && !isCorrectAnswer && <XIcon className="w-5 h-5 ml-2" style={{ color: '#ef4444' }} />}
                        </div>
                    );
                })}
            </div>
            
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                <div className="flex items-center justify-between mb-3">
                    <h4 style={{ fontWeight: '600', color: '#1f2937' }}>Analysis</h4>
                    {(question.Solution || question.solution) && (
                        <button
                            onClick={() => setShowExplanation(!showExplanation)}
                            className="text-sm text-brand-primary hover:text-brand-primary-hover font-medium"
                        >
                            {showExplanation ? 'Hide' : 'Show'} Explanation
                        </button>
                    )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                        <p style={{ color: '#6b7280' }}>Your Answer:</p>
                        <div className={`font-semibold ${
                            isCorrect ? 'text-green-600' : isSkipped ? 'text-gray-600' : 'text-red-600'
                        }`}>
                            {isSkipped ? 'Not Answered' : parseTextWithImages(userAnswer)}
                        </div>
                    </div>
                    <div>
                        <p style={{ color: '#6b7280' }}>Correct Answer:</p>
                        <div style={{ fontWeight: '600', color: '#059669' }}>{parseTextWithImages(correctAnswer)}</div>
                    </div>
                </div>
                
                {showExplanation && (question.Solution || question.solution || question.explanation) && (
                    <div className="pt-3 border-t border-brand-border">
                        <p style={{ fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>Explanation:</p>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {parseTextWithImages(question.Solution || question.solution || question.explanation)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


export const DetailedTestResults = ({ results, test, onBack }) => {
    if (!test || (!test.Questions && !test.questions)) {
        return <div>Test data is not available.</div>;
    }

    const questions = test.Questions || test.questions || [];
    const answers = results?.answers || {};
    const [filter, setFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);

    const questionsPerPage = 5;

    const getQuestionStatus = (question, userAnswer) => {
        if (userAnswer === undefined || userAnswer === null) return 'skipped';
        
        let correctAnswer = question.Answer || question.answer || question.correct;
        if (correctAnswer && correctAnswer.startsWith('option')) {
            const optionNum = parseInt(correctAnswer.replace('option', '')) - 1;
            const options = question.Options || question.options || [
                question.option1, question.option2, question.option3, question.option4
            ].filter(Boolean);
            correctAnswer = options[optionNum];
        }
        
        return userAnswer === correctAnswer ? 'correct' : 'incorrect';
    };

    const filteredQuestions = questions.filter((q, index) => {
        const userAnswer = answers[index] || answers[q.id];
        const status = getQuestionStatus(q, userAnswer);
        if (filter === 'all') return true;
        return status === filter;
    });





    const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);
    const startIndex = (currentPage - 1) * questionsPerPage;
    const paginatedQuestions = filteredQuestions.slice(startIndex, startIndex + questionsPerPage);
    
    const questionsToRender = paginatedQuestions;

    const stats = {
        correct: questions.filter((q, i) => {
            const userAnswer = answers[i] || answers[q.id];
            return getQuestionStatus(q, userAnswer) === 'correct';
        }).length,
        incorrect: questions.filter((q, i) => {
            const userAnswer = answers[i] || answers[q.id];
            return getQuestionStatus(q, userAnswer) === 'incorrect';
        }).length,
        skipped: questions.filter((q, i) => {
            const userAnswer = answers[i] || answers[q.id];
            return getQuestionStatus(q, userAnswer) === 'skipped';
        }).length
    };
    
    if (questions.length === 0) {
        return <div>No questions found in test data.</div>;
    }



    return (
        <div style={{ backgroundColor: '#f9fafb', color: '#1f2937', minHeight: '100vh' }}>
            <header className="sticky top-0 z-10 bg-brand-surface/95 backdrop-blur-md border-b border-brand-border shadow-sm">
                <div className="max-w-7xl mx-auto p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4">
                            <button 
                                onClick={onBack} 
                                className="flex items-center space-x-2 text-sm font-semibold text-brand-text-secondary hover:text-brand-primary transition-colors"
                            >
                                <ArrowLeftIcon className="w-5 h-5" />
                                <span>Back to Summary</span>
                            </button>
                            <button 
                                onClick={() => {
                                    // Ensure data is properly stored with consistent structure
                                    const testDataToStore = {
                                        ...test,
                                        Questions: questions,
                                        questions: questions, // Store both for compatibility
                                        name: test.name || `Test ${test.id}`,
                                        id: test.id
                                    };
                                    localStorage.setItem(`testResults_${test.id}`, JSON.stringify(results));
                                    localStorage.setItem(`testData_${test.id}`, JSON.stringify(testDataToStore));
                                    window.open(`/test/${test.id}/print-results`, '_blank');
                                }}
                                className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors"
                            >
                                <DownloadIcon className="w-4 h-4" />
                                <span>Download PDF</span>
                            </button>
                        </div>
                        <div className="text-center">
                            <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>{test.name || test.Name}</h1>
                            <p className="text-sm text-brand-text-secondary">Detailed Question Analysis</p>
                        </div>
                        <div className="text-right">
                            <div className="text-lg font-bold text-brand-primary">{results.score} / {test.totalMarks || (questions.length * 4)}</div>
                            <div className="text-xs text-brand-text-secondary">{results.accuracy}% Accuracy</div>
                        </div>
                    </div>
                    
                    {/* Filter Tabs */}
                    <div className="flex flex-wrap gap-2">
                        {[
                            { key: 'all', label: 'All Questions', count: questions.length },
                            { key: 'correct', label: 'Correct', count: stats.correct },
                            { key: 'incorrect', label: 'Incorrect', count: stats.incorrect },
                            { key: 'skipped', label: 'Skipped', count: stats.skipped }
                        ].map(({ key, label, count }) => (
                            <button
                                key={key}
                                onClick={() => {
                                    setFilter(key);
                                    setCurrentPage(1);
                                }}
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    filter === key
                                        ? 'bg-brand-primary text-white'
                                        : 'bg-brand-bg text-brand-text-secondary hover:bg-brand-primary/10 hover:text-brand-primary'
                                }`}
                            >
                                {label} ({count})
                            </button>
                        ))}
                    </div>
                </div>
            </header>
            
            <main id="detailed-results-content" className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">

                {filteredQuestions.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-brand-text-secondary">No questions found for the selected filter.</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-6">
                            {questionsToRender.map((q, index) => {
                                const originalIndex = questions.findIndex(question => question.id === q.id);
                                const userAnswer = answers[originalIndex] || answers[q.id];
                                return (
                                    <QuestionResultCard 
                                        key={q.id}
                                        question={q}
                                        userAnswer={userAnswer}
                                        questionIndex={originalIndex}
                                    />
                                );
                            })}
                        </div>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 mt-8">
                                <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium rounded-lg border border-brand-border hover:bg-brand-surface disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                
                                <div className="flex space-x-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                                currentPage === page
                                                    ? 'bg-brand-primary text-white'
                                                    : 'border border-brand-border hover:bg-brand-surface'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>
                                
                                <button
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium rounded-lg border border-brand-border hover:bg-brand-surface disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};